import type { AppState } from 'types/app';
import { selectUnleashToggle } from 'selectors/features';
import { FEATURE_ENABLED_VARIANT_NAME_TREATMENT, FEATURE_ZCS_PTC_BASIC, FEATURE_ZCS_PTC_CATALOG } from 'constants/features';

function isFeatureEnabled(featureName: string, state: AppState): boolean {
  const toggle = selectUnleashToggle(featureName, state);
  return Boolean(toggle?.enabled && toggle?.variant?.name === FEATURE_ENABLED_VARIANT_NAME_TREATMENT);
}

/**
 * Maps Marty gender facet values (title-case) to PTC business unit.
 * Facet values: "Women", "Men", "Boys", "Girls", "Unisex"
 */
export function mapGenderToBusinessUnit(gender: string | null | undefined): string | undefined {
  if (!gender) return undefined;
  switch (gender.toLowerCase()) {
    case 'men':
      return 'mens';
    case 'women':
      return 'womens';
    case 'unisex':
      return 'unisex';
    case 'boys':
      return 'boys';
    case 'girls':
      return 'girls';
    default:
      return undefined;
  }
}

/**
 * Maps zc1 facet value (title-case) to PTC category1.
 * Returns undefined for unmapped types like "Gift Cards".
 */
export function mapTypeToCategory1(type: string | null | undefined): string | undefined {
  if (!type) return undefined;
  const lower = type.toLowerCase();
  switch (lower) {
    case 'shoes':
    case 'clothing':
    case 'accessories':
    case 'watches':
    case 'home':
    case 'bags':
    case 'jewelry':
    case 'eyewear':
    case 'beauty':
    case 'electronics':
    case 'sporting goods':
    case 'baby essentials':
      return lower;
    default:
      return undefined;
  }
}

export function buildPtcBasicHeaders(state: AppState): Record<string, string> {
  if (!isFeatureEnabled(FEATURE_ZCS_PTC_BASIC, state)) return {};

  const isMobile = state.headerFooter?.isMobile;
  const result: Record<string, string> = {
    'zappos-platform': isMobile ? 'mobileWeb' : 'desktop'
  };

  const referrer = state.client?.request?.clientReferer?.trim();
  if (referrer) {
    result['Referrer'] = referrer;
  }

  return result;
}

interface CatalogContext {
  gender: string | null | undefined;
  productType: string | null | undefined;
  category: string | null | undefined;
}

export function buildPtcCatalogHeaders(state: AppState, catalogContext: CatalogContext): Record<string, string> {
  if (!isFeatureEnabled(FEATURE_ZCS_PTC_CATALOG, state)) return {};

  const { gender, productType, category } = catalogContext;
  const result: Record<string, string> = {};

  const businessUnit = mapGenderToBusinessUnit(gender);
  if (businessUnit) result['zappos-business-unit'] = businessUnit;

  const category1 = mapTypeToCategory1(productType);
  if (category1) result['zappos-category1'] = category1;

  if (category) result['zappos-category2'] = category.toLowerCase();

  return result;
}

export function buildAllPtcHeaders(state: AppState, catalogContext?: CatalogContext): Record<string, string> {
  const basic = buildPtcBasicHeaders(state);
  if (!catalogContext) return basic;
  const catalog = buildPtcCatalogHeaders(state, catalogContext);
  return { ...basic, ...catalog };
}
